import { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

import {
  clearSessionTokens,
  getAccessToken,
  isAccessTokenValid,
  isRefreshTokenValid,
} from 'utils/sessionTokens';

import { refreshSession } from './refreshSession';

export const ACCESS_TOKEN_REFRESH_SKEW_MS = 60 * 1000;

export type SessionRequestConfig = AxiosRequestConfig & {
  skipAuthInject?: boolean;
  sessionRefreshRetried?: boolean;
};

const REFRESH_EXEMPT_URL_FRAGMENTS = [
  '/token/refresh',
  '/login',
  '/signup',
  '/verify-mfa',
  '/auth/mfa/',
  '/user/reset-password',
  '/user/change-password',
  '/user/set-new-password',
  '/guest/login',
];

export function isRefreshExemptUrl(url?: string): boolean {
  if (!url) {
    return false;
  }
  return REFRESH_EXEMPT_URL_FRAGMENTS.some((fragment) => url.includes(fragment));
}

export function applyAccessTokenToRequest(config: AxiosRequestConfig, token: string) {
  const headers = (config.headers || {}) as AxiosRequestHeaders;
  const previous = (headers.authorization || headers.Authorization) as string | undefined;
  headers.authorization =
    typeof previous === 'string' && previous.toLowerCase().startsWith('bearer ')
      ? `Bearer ${token}`
      : token;
  config.headers = headers;
}

export async function ensureFreshAccessToken(): Promise<void> {
  if (isAccessTokenValid(ACCESS_TOKEN_REFRESH_SKEW_MS)) {
    return;
  }
  if (!isRefreshTokenValid()) {
    return;
  }
  try {
    await refreshSession();
  } catch {
    // Fall through; a subsequent 401 is handled by the response interceptor.
  }
}

function redirectToLogin() {
  if (window.location.pathname.includes('create')) {
    return;
  }
  const isSignUpPage = window.location.pathname === '/signup';
  window.location.href = isSignUpPage ? '/signup' : '/';
}

export function attachSessionRefreshInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(async (config) => {
    const sessionConfig = config as SessionRequestConfig;
    if (!sessionConfig.skipAuthInject && !isRefreshExemptUrl(config.url)) {
      await ensureFreshAccessToken();
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config as SessionRequestConfig | undefined;

      if (status !== 401 || !original) {
        throw error;
      }

      if (
        original.sessionRefreshRetried ||
        original.skipAuthInject ||
        isRefreshExemptUrl(original.url)
      ) {
        throw error;
      }

      original.sessionRefreshRetried = true;

      if (!isRefreshTokenValid()) {
        clearSessionTokens();
        redirectToLogin();
        throw error;
      }

      try {
        await refreshSession();
        const token = getAccessToken();
        if (token) {
          applyAccessTokenToRequest(original, token);
        }
        return await instance.request(original);
      } catch (refreshError: any) {
        if (refreshError?.response?.status === 401 || !isRefreshTokenValid()) {
          clearSessionTokens();
          redirectToLogin();
        }
        throw error;
      }
    },
  );
}
